import "../../candidate-intelligence/results/styles.css";
import type { EvaluationSetView } from "@shared/labs/level-assessment-evaluation-set";
import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { rpcClient } from "@/lib/rpc";
import { ActiveModelProvider, useActiveModel } from "../active-model-context";
import { ModelPicker } from "../model-picker";
import { C, DM, UI } from "../results/ui-tokens";
import { Card, fmtDate, Muted, statusChipProps } from "./ui";

/**
 * Multi-candidate evaluation sets — the "pacote de candidatos" surface. Lists
 * the active model's sets and creates a new one from a batch of LinkedIn links.
 * Per active model (same scoping as the calibration data hub).
 */
export default function LevelAssessmentSets() {
  return (
    <ActiveModelProvider>
      <SetsHub />
    </ActiveModelProvider>
  );
}

const MAX_PROFILES = 20;

function SetsHub() {
  const { modelId, model, disciplineId, setDisciplineId } = useActiveModel();
  const [, navigate] = useLocation();

  const [sets, setSets] = useState<EvaluationSetView[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [urlsText, setUrlsText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setSets(null);
    setListError(null);
    (async () => {
      try {
        const res = (await rpcClient.labs.levelAssessmentSetsList({ modelId })) as {
          sets: EvaluationSetView[];
        };
        if (!cancelled) setSets(res.sets);
      } catch (e) {
        if (!cancelled) setListError(e instanceof Error ? e.message : "Erro inesperado");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [modelId]);

  const needsDiscipline = (model?.disciplines.length ?? 0) > 0;

  function parseUrls(text: string): string[] {
    return text
      .split("\n")
      .map((l) => l.trim())
      .filter(Boolean);
  }

  async function onCreate() {
    setFormError(null);
    const trimmedName = name.trim();
    const urls = parseUrls(urlsText);
    if (!trimmedName) return setFormError("Dê um nome ao pacote.");
    if (urls.length === 0) return setFormError("Cole ao menos um link do LinkedIn.");
    if (urls.length > MAX_PROFILES)
      return setFormError(
        `Máximo de ${MAX_PROFILES} candidatos por pacote (você colou ${urls.length}).`,
      );
    if (needsDiscipline && !disciplineId) return setFormError("Escolha uma disciplina.");

    setSubmitting(true);
    try {
      const res = (await rpcClient.labs.levelAssessmentSetCreate({
        name: trimmedName,
        modelId,
        disciplineId: needsDiscipline ? disciplineId : undefined,
        note: note.trim() || undefined,
        profiles: urls.map((u) => ({ linkedinUrl: u })),
      })) as { set: EvaluationSetView };
      navigate(`/labs/level-assessment/sets/${res.set.id}`);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : "Falha ao criar o pacote.");
      setSubmitting(false);
    }
  }

  const urlCount = parseUrls(urlsText).length;
  const inputStyle: React.CSSProperties = {
    ...UI,
    width: "100%",
    fontSize: 14,
    color: C.ink,
    background: C.white,
    border: `1px solid ${C.ink10}`,
    borderRadius: 10,
    padding: "10px 12px",
    outline: "none",
  };

  return (
    <div className="ci-stripe ci-stripe-page">
      <div className="ci-narrow fade-up" style={{ width: "100%", maxWidth: 1000 }}>
        <Link
          href="/labs/level-assessment"
          style={{
            ...UI,
            fontSize: 12,
            color: C.ink50,
            textDecoration: "none",
            letterSpacing: "0.04em",
            textTransform: "uppercase",
            fontWeight: 600,
          }}
        >
          ← Voltar para avaliação
        </Link>
        <h1
          style={{
            ...DM,
            margin: "8px 0 6px",
            fontSize: 28,
            fontWeight: 700,
            color: C.ink,
            letterSpacing: "-0.015em",
          }}
        >
          Pacotes de candidatos
        </h1>
        <p
          style={{
            ...UI,
            margin: 0,
            fontSize: 14,
            color: C.ink50,
            lineHeight: 1.55,
            maxWidth: 760,
          }}
        >
          Avalie vários candidatos de uma vez. Cole os links do LinkedIn, nomeie o pacote, e o
          modelo
          {model ? ` “${model.name}”` : ""} estima o nível de cada um — você revisa e exporta o
          conjunto.
        </p>
        <div style={{ marginTop: 14 }}>
          <ModelPicker />
        </div>

        {/* Create form */}
        <Card style={{ marginBottom: 28 }}>
          <div style={{ ...DM, fontSize: 16, fontWeight: 700, color: C.ink, marginBottom: 12 }}>
            Novo pacote
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
              <label
                style={{ flex: "1 1 240px", display: "flex", flexDirection: "column", gap: 4 }}
              >
                <span style={{ ...UI, fontSize: 12, fontWeight: 600, color: C.ink70 }}>Nome *</span>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="ex. Forward Deployed · Q3"
                  style={inputStyle}
                />
              </label>
              <label
                style={{ flex: "1 1 200px", display: "flex", flexDirection: "column", gap: 4 }}
              >
                <span style={{ ...UI, fontSize: 12, fontWeight: 600, color: C.ink70 }}>
                  Referência (opcional)
                </span>
                <input
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  placeholder="ex. #FD-204"
                  style={inputStyle}
                />
              </label>
              {needsDiscipline && (
                <label
                  style={{ flex: "1 1 200px", display: "flex", flexDirection: "column", gap: 4 }}
                >
                  <span style={{ ...UI, fontSize: 12, fontWeight: 600, color: C.ink70 }}>
                    Disciplina *
                  </span>
                  <Select
                    value={disciplineId ?? ""}
                    onValueChange={(v) => setDisciplineId(v || null)}
                  >
                    <SelectTrigger style={{ height: 40, fontSize: 14 }}>
                      <SelectValue placeholder="Escolher disciplina" />
                    </SelectTrigger>
                    <SelectContent>
                      {model?.disciplines.map((d) => (
                        <SelectItem key={d.id} value={d.id}>
                          {d.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </label>
              )}
            </div>
            <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ ...UI, fontSize: 12, fontWeight: 600, color: C.ink70 }}>
                Links do LinkedIn — um por linha (até {MAX_PROFILES})
              </span>
              <textarea
                value={urlsText}
                onChange={(e) => setUrlsText(e.target.value)}
                rows={6}
                placeholder={
                  "https://linkedin.com/in/candidato-1\nhttps://linkedin.com/in/candidato-2"
                }
                style={{ ...inputStyle, resize: "vertical", fontFamily: "inherit" }}
              />
              <span
                style={{ ...UI, fontSize: 11.5, color: urlCount > MAX_PROFILES ? C.red : C.ink50 }}
              >
                {urlCount} / {MAX_PROFILES} candidatos
              </span>
            </label>
            {formError && <div style={{ ...UI, fontSize: 13, color: C.red }}>{formError}</div>}
            <div>
              <button
                type="button"
                onClick={onCreate}
                disabled={submitting}
                style={{
                  ...DM,
                  border: 0,
                  cursor: submitting ? "default" : "pointer",
                  borderRadius: 999,
                  fontWeight: 700,
                  fontSize: 14,
                  padding: "10px 20px",
                  background: submitting ? C.ink20 : C.red,
                  color: C.white,
                }}
              >
                {submitting ? "Criando…" : "Avaliar pacote"}
              </button>
            </div>
          </div>
        </Card>

        {/* List */}
        <div style={{ ...DM, fontSize: 16, fontWeight: 700, color: C.ink, marginBottom: 10 }}>
          Pacotes anteriores
        </div>
        {listError && <Muted>Falha ao carregar os pacotes: {listError}</Muted>}
        {!sets && !listError && <Muted>Carregando…</Muted>}
        {sets && sets.length === 0 && <Muted>Nenhum pacote ainda — crie o primeiro acima.</Muted>}
        {sets && sets.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {sets.map((s) => {
              const chip = statusChipProps(s.status);
              return (
                <Link
                  key={s.id}
                  href={`/labs/level-assessment/sets/${s.id}`}
                  style={{ textDecoration: "none" }}
                >
                  <Card
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 14,
                      padding: "14px 18px",
                      cursor: "pointer",
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ ...DM, fontSize: 15, fontWeight: 600, color: C.ink }}>
                        {s.name}
                      </div>
                      <div style={{ ...UI, fontSize: 12, color: C.ink50, marginTop: 2 }}>
                        {s.note ? `${s.note} · ` : ""}
                        {s.expectedCount} candidatos · {fmtDate(s.createdAt)}
                      </div>
                    </div>
                    <span
                      style={{
                        ...UI,
                        fontSize: 11.5,
                        fontWeight: 600,
                        color: chip.fg,
                        background: chip.bg,
                        padding: "3px 9px",
                        borderRadius: 999,
                      }}
                    >
                      {chip.label}
                    </span>
                  </Card>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
